import { PoweredByFooter } from '../components/PoweredByFooter.tsx';
import { COPY } from '../copy/vi.ts';

/**
 * L-001 — M05 "đã lưu trên thiết bị" and M06 "xác minh lại" (§15, §16).
 *
 * Amber, never green: the data exists on the phone and nothing more. The
 * participant is told plainly that the server has not confirmed anything,
 * because the alternative — a reassuring screen for a submission that is
 * still sitting in IndexedDB — is how a venue ends up with people who think
 * they checked in and did not.
 */
export function PendingScreen({
  variant,
  locationName,
  pendingCount,
  isOnline,
  retrying,
  onRetry,
}: {
  variant: 'pending' | 'verify_retry';
  /** Null when the event has no real location, so none was ever shown. */
  locationName: string | null;
  pendingCount: number;
  isOnline: boolean;
  retrying: boolean;
  onRetry: () => void;
}) {
  const isVerify = variant === 'verify_retry';

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-[480px] flex-col justify-between bg-bg px-5 py-10">
      <div
        role="status"
        aria-live="polite"
        className="flex flex-1 flex-col items-center justify-center gap-5 text-center"
      >
        <div
          className="flex size-20 items-center justify-center rounded-full bg-warning-soft text-4xl text-warning"
          aria-hidden="true"
        >
          {isVerify ? '!' : '☁'}
        </div>

        <h1 className="text-xl font-extrabold tracking-wide text-warning">
          {isVerify ? COPY.verifyRetry.title : COPY.pending.title}
        </h1>

        {isVerify ? null : (
          <div className="flex flex-col gap-2">
            <p className="text-base text-text-muted">{COPY.pending.line1}</p>
            <p className="text-base text-text-muted">{COPY.pending.line2}</p>
          </div>
        )}

        {locationName ? (
          <div className="flex flex-col gap-1 pt-1">
            <span className="text-xs font-semibold tracking-wide text-text-muted uppercase">
              {COPY.accepted.locationLabel}
            </span>
            <span className="text-base font-bold">{locationName}</span>
          </div>
        ) : null}

        {pendingCount > 1 ? (
          <p className="text-sm font-semibold text-text-muted">
            {COPY.recovery.manyPrefix} {pendingCount}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="h-14 rounded-control bg-warning text-[17px] font-extrabold tracking-wide text-white disabled:opacity-60"
        >
          {retrying
            ? COPY.sending.verifying
            : isVerify
              ? COPY.verifyRetry.action
              : COPY.pending.retryNow}
        </button>

        {/* §15 — the button stays enabled offline; the explanation is here so a
            tap that cannot work does not read as a broken button. */}
        {isOnline ? null : (
          <p className="text-center text-sm font-semibold text-text-muted">
            {COPY.pending.offlineHint}
          </p>
        )}

        <PoweredByFooter />
      </div>
    </div>
  );
}
